import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Icon } from 'cfa-react-components';
import { useCallback, useMemo, useState } from 'react';
import CFASpinner from '../../components/Widget/CFASpinner/CFASpinner';
import { useGetAllVendorInvitationsQuery } from '../../store/invitationsApi';
import Template from '../Template/Template';
import styles from './ListInvites.module.scss';

function getColumnSortCompareFn(sortState) {
  return function getColumnSortCompareFn(a, b) {
    const aVal = a[sortState.column];
    const bVal = b[sortState.column];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    return sortState.ascending
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  };
}
const ListInvites = () => {
  const { data: allInvitations = [], isLoading } =
    useGetAllVendorInvitationsQuery({
      refetchOnMountOrArgChange: true
    });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortState, setSortState] = useState({
    column: 'email',
    ascending: true
  });

  const sortedData = useMemo(
    () =>
      allInvitations
        ? [...allInvitations].sort(getColumnSortCompareFn(sortState))
        : [],
    [allInvitations, sortState]
  );

  const displayData = useMemo(
    () =>
      sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, sortedData]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getHandleSortClick = (columnId) =>
    function handleSortClick() {
      setSortState((prev) => {
        if (prev.column === columnId) {
          return {
            column: prev.column,
            ascending: !prev.ascending
          };
        }
        return {
          column: columnId,
          ascending: true
        };
      });
    };

  const getAriaSort = useCallback(
    (columnId) => {
      if (typeof columnId !== 'string')
        throw new TypeError('columnId must be a string');

      if (columnId !== sortState.column) return 'none';

      return sortState.ascending ? 'ascending' : 'descending';
    },
    [sortState]
  );

  const componentIsFullyLoaded = !isLoading;

  const columns = [
    { id: 'email', label: 'Email', minWidth: 70, align: 'left' },
    { id: 'status', label: 'Status', minWidth: 70, align: 'left' },
    { id: 'vendorName', label: 'Vendor Name', maxWidth: 70, align: 'left' }
  ];

  let content;

  if (!componentIsFullyLoaded) {
    content = <CFASpinner />;
  } else {
    content = (
      <Paper
        sx={{ width: '90%', overflow: 'hidden', margin: 'auto' }}
        variant="outlined"
      >
        <>
          <h2 className={`${styles['all-vendor-invites-header']}`}>
            All User Invites
          </h2>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        color: '#5b6770',
                        cursor: 'pointer'
                      }}
                      onClick={getHandleSortClick(column.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          getHandleSortClick(column.id)();
                        }
                      }}
                      role="columnheader"
                      tabIndex={0}
                      aria-sort={getAriaSort(column.id)}
                    >
                      <h3>
                        {column.label}

                        {sortState.column === column.id && (
                          <Icon
                            icon={
                              sortState.ascending
                                ? 'chevron-up'
                                : 'chevron-down'
                            }
                            style={{ paddingLeft: '6px' }}
                          />
                        )}
                      </h3>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.map((invitation) => (
                  <TableRow
                    key={invitation.invitationKey || invitation.email}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    <TableCell>{invitation.email}</TableCell>
                    <TableCell>{invitation.status}</TableCell>
                    <TableCell>{invitation.vendorName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            style={{ display: 'flex', justifyContent: 'center' }}
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={allInvitations?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      </Paper>
    );
  }
  return <Template>{content}</Template>;
};

export default ListInvites;
